import React, { useEffect, useState } from 'react';
import { H3, Link, Shimmer, ShimmerPlaceholderText } from '../../../../../ui';
import styled from '@emotion/styled';
import { IParkingLocation } from '../../types/IParkingLocation';
import { useUrlForPageId } from '../../../../app/pages/ui/utils/useUrlForPageId';
import { PASSENGERS_PARKING_LANDING_PAGE_ID } from '../../../../shared/api/contentful/constants';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { ParkingAvailabilityStatus } from '../../types/ParkingAvailabilityStatus';
import { ParkingAvailabilityTier } from '../../types/ParkingAvailabilityTier';
import { ITranslationFunction } from '../../../../shared/i18n/translate';

interface IParkingAvailabilityItemProps {
	parkingLocation: IParkingLocation;
}

/*
	For an overview of the Parking Availability Flow,
	see https://agencymove.slack.com/archives/C03MWU2V36H/p1750422690282649
*/

export const ParkingAvailabilityItem = ({
	parkingLocation: { name, status, total, available, tier, directionsUrl },
}: IParkingAvailabilityItemProps) => {
	const { t } = useTranslation();
	const parkingUrl = useUrlForPageId(PASSENGERS_PARKING_LANDING_PAGE_ID);
	const [percentLeft, setPercentLeft] = useState(0);
	const headingId = `parking-${name.toLowerCase().replace(/\s+/g, '-')}`;

	useEffect(() => {
		if (available && total) {
			setPercentLeft((available / total) * 100);
		}
	}, [available, total]);

	const getParkingAvailabilityLabel = (
		translation: ITranslationFunction,
		availableParkingSpots: number | null | undefined,
		parkingAvailabilityStatus: ParkingAvailabilityStatus,
	): string => {
		if (
			availableParkingSpots === 0 ||
			parkingAvailabilityStatus === ParkingAvailabilityStatus.CLOSED
		) {
			return translation('live_parking_availability_state_closed');
		}

		return translation('live_parking_availability_state_open_book_online');
	};

	return (
		<StyledItem>
			<StyledTitleContainer>
				<StyledTitle id={headingId}>{name}</StyledTitle>
				<StyledOccupancy
					available={available}
					status={status}
					percentLeft={percentLeft}
				>
					<StyledOccupancyStatus>
						{getParkingAvailabilityLabel(t, available, status)}
					</StyledOccupancyStatus>
				</StyledOccupancy>
			</StyledTitleContainer>
			<StyledSub>
				<Link href={directionsUrl} target="_blank" aria-describedby={headingId}>
					{t('live_parking_availability_subtitle')}
				</Link>
				{tier !== ParkingAvailabilityTier.NEVER && (
					<Link href={parkingUrl} aria-describedby={headingId}>
						{t(
							`live_parking_availability_tier_book_${
								tier === ParkingAvailabilityTier.NOW ? 'now' : 'later'
							}`,
						)}
					</Link>
				)}
			</StyledSub>
		</StyledItem>
	);
};

export const ParkingAvailabilityItemShimmer = () => (
	<Shimmer aria-label="shimmer">
		<StyledItem>
			<StyledShimmerTitleContainer>
				<ShimmerPlaceholderText />
				<ShimmerPlaceholderText />
			</StyledShimmerTitleContainer>
			<StyledShimmerSub>
				<ShimmerPlaceholderText />
				<ShimmerPlaceholderText />
			</StyledShimmerSub>
		</StyledItem>
	</Shimmer>
);

const StyledItem = styled.div`
	display: grid;
	border: 1px solid #0000001a;
	padding: 10px 10px 0 10px;
`;

const StyledTitleContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid #0000001a;
	padding-bottom: 8px;
	gap: 0.5rem;
`;

const StyledTitle = styled(H3)`
	margin: 0;
`;

const StyledOccupancy = styled.div<{
	percentLeft?: number;
	available?: number;
	status?: ParkingAvailabilityStatus;
}>`
	padding: 5px 10px;

	${({ available, status, percentLeft }) =>
		status === ParkingAvailabilityStatus.CLOSED || available === 0
			? `background-color: #FFE2E0;
		color: #CC1100;`
			: status === ParkingAvailabilityStatus.WARNING ||
			  (percentLeft &&
					percentLeft < 15 &&
					status === ParkingAvailabilityStatus.OPEN)
			? `background-color: #FBF0D0;
			color: #7E610C;`
			: `background-color: #D9F5DD;
		color: #007500;`}
`;

const StyledOccupancyStatus = styled.span`
	font-weight: 700;

	&:after {
		content: ' ';
	}
`;

const StyledSub = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: 1rem;
	padding: 0.2rem 0;
`;

const StyledShimmerTitleContainer = styled(StyledTitleContainer)`
	gap: 24px;
`;

const StyledShimmerSub = styled(StyledSub)`
	gap: 24px;
	padding: 0.5rem 0;
`;
